import { QueryClient, UseMutationResult } from 'react-query';
import {
    addVenueUserApi,
    getJobRolesApi,
    getPermissionGroupsForVenueApi,
    getVenueUsersByIdApi,
    getVenueUsersListApi,
    updateVenueUserStatusApi,
    usersBulkCopyApi,
} from '../constants/apiEndPoints';
import HttpServiceHelper from './httpServiceHelper';
import { _HttpMethods, _Payload, _UserType } from '../constants/staticTypes';
import * as Yup from 'yup';
import { AxiosResponse } from 'axios';
import { getErrorMessage } from '../utils/common';
import { AlertVariant } from '../constants/constants';

export const getVenueUsersList = (queryClient: QueryClient, page: number, limit: number, search?: string, xTenantId?: string) => {
    let url = `${getVenueUsersListApi}?limit=${limit}&page=${page}`;
    if (search) {
        url += search;
    }

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
        xTenantId,
    });
};

export type _ActiveTenant = {
    id: string;
    tenantName: string;
};

export type _VenueUser = {
    id: string;
    tenantId: string;
    userId: string;
    hireDate: string | null;
    posNumber: string;
    isOrgAdmin: boolean;
    isActive: boolean;
    firstName: string;
    lastName: string;
    preferredName: string;
    email: string | null;
    loginUserName: string;
    preferredUserName: string;
    phoneNumber: string;
    photoKey: string;
    signedUrl: string;
    photoThumbnail: string;
    createdAt: string;
    updatedAt: string;
    preferredLangKey: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    activeTenants: _ActiveTenant[];
    staffPermissionRoles: string[];
    jobRoles: string[];
    permissionRoles: any[];
};

export type _AddUserVenueFormType = {
    loginUserName: string;
    password: string;
    preferredName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    hireDate: string | null;
    staffPermissionRoles: string;
    photoKey: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    sendPasswordCreationTo: 'phone' | 'email';
    userType: _UserType;
    id?: string; // to be used when editing staff
    signedUrl?: string;
    jobRoles: string[];
    posNumber: string;
};

export const initialAddUserVenueDataValues: _AddUserVenueFormType = {
    loginUserName: '',
    password: '',
    preferredName: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    hireDate: '',
    staffPermissionRoles: '',
    photoKey: '',
    sendPasswordCreation: true,
    passwordLessLoginOnly: true,
    sendPasswordCreationTo: 'email',
    userType: 'organizationStaff',
    signedUrl: '',
    jobRoles: [],
    posNumber: '',
};

export const addUserVenueValidationSchema = Yup.object({
    loginUserName: Yup.string().required('Username is required'),
    sendPasswordCreation: Yup.boolean(),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
    preferredName: Yup.string().required('Display name is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
});

export type _AddVenueForm = {
    venue: {
        id: string;
        venueName: string;
    };
    permissionGroup: {
        id: string;
        permissionRoleName: string;
    };
    Roles: {
        id: string;
        RoleName: string;
    };
    hireDate: string;
    POS: string;
};

export const orgSelectVenueFormInitialValues: _AddVenueForm = {
    venue: {
        id: '',
        venueName: '',
    },
    permissionGroup: {
        id: '',
        permissionRoleName: '',
    },
    Roles: {
        id: '',
        RoleName: '',
    },
    hireDate: '',
    POS: '',
};

export const orgSelectVenueFormvalidationSchema = Yup.object({
    venue: Yup.object({
        id: Yup.string().required('Venue is required'),
        venueName: Yup.string(),
    }),
    permissionGroup: Yup.object({
        id: Yup.string(),
        permissionRoleName: Yup.string(),
    }),
});

export type _VenueUserData = {
    loginUserName: string;
    password: string;
    preferredName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    hireDate: string | null;
    staffPermissionRoles: string[];
    photoKey: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    sendPasswordCreationTo: 'phone' | 'email';
    userType?: _UserType;
    id?: string; // to be used when editing staff
    signedUrl?: string;
    jobRoles: string[];
    posNumber: string;
};

export const addVenueUser = (
    addVenueUserMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _VenueUserData,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    addVenueUserMutation.mutate(
        {
            url: addVenueUserApi,
            method: _HttpMethods.POST,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const getAddVenueUserApiFormattedData = (formData: _AddUserVenueFormType) => {
    const { userType, ...rest } = formData;
    return {
        ...rest,
        staffPermissionRoles: [formData.staffPermissionRoles],
    };
};

export const getVenuePermissionGroupList = (queryClient: QueryClient, tenantId: string, limit = 500) => {
    const url = `${getPermissionGroupsForVenueApi}/${tenantId}?limit=${limit}`;
    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

export const getVenueJobRolesList = (queryClient: QueryClient, tenantId: string) => {
    const url = `${getJobRolesApi}`;
    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
        xTenantId: tenantId,
    });
};

export type _JobRole = {
    id: string;
    jobRoleName: string;
};

export type _VenueUserID = {
    id: string;
};

export const getVenueUserDetailByID = (
    getVenueUserDetailByIDMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _VenueUserID,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    getVenueUserDetailByIDMutation.mutate(
        {
            url: getVenueUsersByIdApi,
            method: _HttpMethods.POST,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const editVenueUser = (
    addVenueUserMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _VenueUserData,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    addVenueUserMutation.mutate(
        {
            url: addVenueUserApi,
            method: _HttpMethods.PUT,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const deleteVenueUserDetailByID = (
    getAdminStaffDetailByIDMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _VenueUserID,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    getAdminStaffDetailByIDMutation.mutate(
        {
            url: addVenueUserApi,
            method: _HttpMethods.DELETE,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export type _UpdateStaffStatus = {
    id: string;
    newStatus: boolean;
};

export const updateVenueUserStatus = (
    updateAdminStaffStatusMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdateStaffStatus,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    updateAdminStaffStatusMutation.mutate(
        {
            url: updateVenueUserStatusApi,
            method: _HttpMethods.POST,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const buildUserCopyPayload = (selectedData: any) => {
    return selectedData.map((item: any) => item.id);
};

export type _UsersBulkCopyPayloadType = {
    tenantId: string;
    newTenantId: string;
    tenantStaffIds: string[];
};

export const bulkCopyUsers = (
    usersBulkCopyMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UsersBulkCopyPayloadType,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    usersBulkCopyMutation.mutate(
        {
            url: usersBulkCopyApi,
            method: _HttpMethods.POST,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};
